// ════════════════════════════════════════════════════════════
//  WorldChat.jsx — the chat pill + panel over a fight you're watching
// ════════════════════════════════════════════════════════════
//  Collapsed it is one small pill (a dot when someone spoke while it was shut);
//  Enter opens it with the cursor in the box. While you're typing, NO key reaches the
//  game — Esc closes the box instead of leaving the fight.
// ════════════════════════════════════════════════════════════
import { useEffect, useRef, useState } from "react";
import { useWorldChat, CHAT } from "../online/worldChat.js";
import { sayOnSign } from "../online/skybox.js";
import { serverNow } from "../online/ie.js";
import { say } from "../data/text.js";
import HideBtn from "./HideBtn.jsx";

export default function WorldChat({ on }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [unread, setUnread] = useState(false);
  const box = useRef(null), list = useRef(null), seen = useRef(0), joined = useRef(serverNow());
  const { messages, send, slow } = useWorldChat({ uid: on && on.authUid, name: on && on.name, canSend: !!(on && on.writable), enabled: !!(on && on.authUid) });

  // a message from someone else that lands while it's shut lights the dot (history from before you sat down doesn't)
  useEffect(() => {
    const m = messages.length ? messages[messages.length - 1] : null;
    if (open) { if (m) seen.current = m.ts; setUnread(false); }
    else if (m && m.ts > Math.max(seen.current, joined.current) && !(on && m.uid === on.authUid)) setUnread(true);
  }, [messages, open, on]);
  useEffect(() => { if (open && list.current) list.current.scrollTop = list.current.scrollHeight; }, [messages, open]);
  useEffect(() => { if (open && box.current) box.current.focus(); }, [open]);

  // Enter opens it from anywhere (not while another box has the keys)
  useEffect(() => {
    const down = (e) => {
      const t = e.target;
      if (e.key !== "Enter" || e.repeat || (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "BUTTON"))) return;   // Enter on a button presses the button, nothing else
      setOpen(true);
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, []);

  // what you say also goes up on your sign in the Sky Box (caP'n 2026-10-17: "when a player chats their chat message appears on the sign")
  const submit = () => { const t = text; if (send(t)) { setText(""); sayOnSign(t); } };
  const keys = (e) => {
    e.stopPropagation();   // nothing typed here reaches the game's keys (Esc would leave the fight)
    if (e.key === "Enter") { e.preventDefault(); submit(); }
    else if (e.key === "Escape") { e.preventDefault(); e.currentTarget.blur(); setOpen(false); }
  };

  if (!open) return (
    <>
      <button className={"wc-pill" + (unread ? " unread" : "")} onClick={() => setOpen(true)} title="Enter">{say("chat.title")}{unread && <i />}</button>
      <HideBtn id="chat" className="wc-hide" />
    </>
  );
  return (
    <div className="wc-panel">
      <div className="wc-head"><b>{say("chat.title")}</b><span><HideBtn id="chat" /><button onClick={() => setOpen(false)} title="Esc">✕</button></span></div>
      <div className="wc-list" ref={list}>
        {messages.length === 0 && <div className="wc-empty">{say("chat.empty")}</div>}
        {messages.map((m) => <div key={m.id} className={"wc-msg" + (on && m.uid === on.authUid ? " me" : "")}><b>{m.name}</b> {m.text}</div>)}
      </div>
      {on && on.writable ? (
        <div className="wc-send">
          <input ref={box} value={text} maxLength={CHAT.max} placeholder={say("chat.placeholder")} onChange={(e) => setText(e.target.value)} onKeyDown={keys} onKeyUp={(e) => e.stopPropagation()} />
          <button onClick={submit}>{say("chat.send")}</button>
        </div>
      ) : <div className="wc-note">{say("chat.signin")}</div>}
      {slow && <div className="wc-note">{say("chat.slow")}</div>}
    </div>
  );
}
